import { FC, MouseEvent, useContext, useState } from "react";
import style from "./TeamCard.module.scss";
import { ITeam } from "../../../models/Team";
import Typhography from "../../../shared/Typhography/Typhography";
import { DotsThreeVerticalIcon, StarIcon } from "@phosphor-icons/react";
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Portal,
  Spinner,
} from "@chakra-ui/react";
import classNames from "classnames";
import { useToastAlert } from "../../../hooks/useToastAlert/useToastAlert";
import { IEndpoints } from "../../../hooks/useEndpoints";
import { USER_TYPES } from "../../../enums/user";
import { AuthContext } from "../../../providers/authProvider";
import { ShowByRole } from "../../../shared/ShowByRole";
import { getLabelFromOptions } from "../../../lib/DurationPicker/utils";
import { LEVELS_SPORT_OPTIONS } from "../../../constants/config";
import { SPACE_TYPES } from "../../../enums/Space";

export const TeamCard: FC<{
  team: ITeam & { users: [{ image: string | null; type: USER_TYPES }] };
  patchTeam: IEndpoints["patchTeam"];
  onclickEditButton: (e: any) => void;
  onChangeStart: (id: string, isPreferred: boolean) => void;
  spaceType?: SPACE_TYPES;
}> = ({ team, patchTeam, onChangeStart, onclickEditButton, spaceType }) => {
  const { isAdmin } = useContext(AuthContext);

  const toastAlert = useToastAlert();
  const [isLoading, setIsLoading] = useState(false);
  const coaches = team.users.filter(({ type }) => type === USER_TYPES.COACH);
  const onClickStartIcon = async (event: any) => {
    event.stopPropagation();
    try {
      const isPreferred = !team.isPreferred;
      setIsLoading(true);
      await patchTeam(team._id, { isPreferred });
      onChangeStart(team._id, isPreferred);
      toastAlert({
        message: isPreferred
          ? "Team aggiunto in evidenza"
          : "Team rimosso in evidenza",
      });
    } catch (error) {
      console.error(error);
      toastAlert({ type: "error", message: "Errore" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={classNames(style.container, { [style.containerPreferred]: team.isPreferred })}>
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-2">
          <div className={style.logoContainer}>
            {team.image && (
              <img
                className={style.logoImage}
                src={`${process.env.REACT_APP_BASE_URL_IMAGE}${team.image}`}
              />
            )}
          </div>
          <Typhography size="bold-20">{team.name}</Typhography>
          <div className={style.typeContainer}>
            <Typhography size="regular-12">{getLabelFromOptions(LEVELS_SPORT_OPTIONS[(spaceType ?? "") as SPACE_TYPES] ?? [], team.level) || team.level}</Typhography>
          </div>
          {isLoading ? (
            <Spinner size="xs" />
          ) : team.isPreferred ? (
            <StarIcon
              className={style.starredIcon}
              weight="fill"
              onClick={onClickStartIcon}
            />
          ) : (
            <StarIcon className={style.starIcon} onClick={onClickStartIcon} />
          )}
        </div>
        <ShowByRole roles={["isAdmin", "isDirector"]}>
          <Menu.Root>
            <Menu.Trigger
              as={DotsThreeVerticalIcon}
              asChild
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Button
                as={DotsThreeVerticalIcon}
                className={style.dotsContainer}
              ></Button>
            </Menu.Trigger>
            <Portal>
              <Menu.Positioner>
                <Menu.Content className="ps-2 pe-2">
                  <Menu.Item value="" onClick={onclickEditButton}>
                    Modifica Team
                  </Menu.Item>
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>
        </ShowByRole>
      </div>
      <div className="d-flex align-items-center justify-content-between">
        <Typhography size="medium-12">
          {`${team.users.length}/${team.users.length}`} giocatori attivi
        </Typhography>
        <div className="d-flex align-items-center gap-1">
          <Typhography size="regular-12">Coaches</Typhography>
          <div className={style.containerAvatar}>
            {coaches.slice(0, 2).map((item, i) => {
              return (
                <div
                  key={i}
                  className={style.coachContainer}
                  style={{
                    transform: `translateX(${-6 * (i - 1)}px)`,
                  }}
                >
                  {item.image ? (
                    <img
                      src={`${process.env.REACT_APP_BASE_URL_IMAGE}${
                        (item.image as any).image
                      }`}
                      className={style.avatartImage}
                    />
                  ) : (
                    <Typhography>
                      {item.firstName[0]}
                      {item.lastName[0]}
                    </Typhography>
                  )}
                </div>
              );
            })}
            {coaches.length - 2 > 0 && (
              <div
                className={classNames(
                  style.coachContainer,
                  "d-flex justify-content-center align-items-center"
                )}
                style={{
                  transform: `translateX(${-6 * (2 - 1)}px)`,
                }}
              >
                <Typhography size="bold-14">+{coaches.length}</Typhography>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default TeamCard;
